import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Alert, CircularProgress, Grid, Chip, Divider, FormControlLabel, Switch,
  Tabs, Tab, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ApiIcon from '@mui/icons-material/Api';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';

const API = '/api/contabilidad';

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function PreviewTable({ docs, tipoOp }) {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3, maxHeight: 500, overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Tipo</TableCell>
            <TableCell>Serie-Número</TableCell>
            <TableCell>Fecha</TableCell>
            <TableCell>{tipoOp === 'VENTA' ? 'Cliente' : 'Proveedor'}</TableCell>
            <TableCell>RUC/Doc</TableCell>
            <TableCell align="right">Base Grav.</TableCell>
            <TableCell align="right">IGV</TableCell>
            <TableCell align="right">Exonerada</TableCell>
            <TableCell align="right">Inafecta</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell>Mon.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {docs.slice(0, 200).map((d, i) => (
            <TableRow key={i} hover>
              <TableCell>{d.tipo_documento}</TableCell>
              <TableCell>{d.serie}-{d.numero}</TableCell>
              <TableCell>{d.fecha_emision}</TableCell>
              <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.razon_social}</TableCell>
              <TableCell>{d.ruc_tercero}</TableCell>
              <TableCell align="right">{fmt(d.base_gravada)}</TableCell>
              <TableCell align="right">{fmt(d.igv)}</TableCell>
              <TableCell align="right">{fmt(d.base_exonerada)}</TableCell>
              <TableCell align="right">{fmt(d.base_inafecta)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(d.total)}</TableCell>
              <TableCell><Chip label={d.moneda || 'PEN'} size="small" /></TableCell>
            </TableRow>
          ))}
          {docs.length > 200 && (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                ... mostrando primeros 200 de {docs.length} documentos
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ImportadorSire() {
  // Shared state
  const [tab, setTab] = useState(0);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [empresaConfig, setEmpresaConfig] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [tipoOp, setTipoOp] = useState('VENTA');

  // Excel tab state
  const [generarAsientos, setGenerarAsientos] = useState(true);
  const [file, setFile] = useState(null);
  const [xlsPreview, setXlsPreview] = useState(null);
  const [xlsLoading, setXlsLoading] = useState(false);
  const [xlsResult, setXlsResult] = useState(null);
  const [xlsError, setXlsError] = useState('');

  // API tab state
  const [claveSol, setClaveSol] = useState('');
  const [showClave, setShowClave] = useState(false);
  const [apiPreview, setApiPreview] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiDescargando, setApiDescargando] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) { setEmpresaConfig(null); return; }
    axios.get(`${API}/empresas/${empresaId}`).then(r => setEmpresaConfig(r.data)).catch(() => setEmpresaConfig(null));
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a, b) => b.periodo.localeCompare(a.periodo));
      setPeriodos(r.data);
      if (abiertos.length > 0) setPeriodoSel(abiertos[0].periodo);
    }).catch(() => {});
  }, [empresaId]);

  function handleEmpresaChange(val) {
    setEmpresaId(val);
    setPeriodoSel('');
    setXlsPreview(null); setXlsResult(null); setXlsError(''); setFile(null);
    setApiPreview(null); setApiResult(null); setApiError('');
  }

  // ── Excel tab handlers ──
  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setXlsPreview(null); setXlsResult(null); setXlsError(''); }
  }

  async function handleXlsPreview() {
    if (!file || !empresaId || !periodoSel) { setXlsError('Selecciona empresa, período y archivo'); return; }
    setXlsLoading(true); setXlsError(''); setXlsPreview(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file); fd.append('empresa_id', empresaId);
      fd.append('periodo', periodoSel); fd.append('tipo_operacion', tipoOp);
      fd.append('solo_preview', '1');
      const r = await axios.post(`${API}/importar-sire`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setXlsPreview(r.data);
    } catch (e) { setXlsError(e.response?.data?.error || 'Error al procesar el archivo'); }
    setXlsLoading(false);
  }

  async function handleXlsImportar() {
    if (!file || !empresaId || !periodoSel) { setXlsError('Selecciona empresa, período y archivo'); return; }
    if (!window.confirm(`¿Importar ${xlsPreview?.total || ''} documentos?`)) return;
    setXlsLoading(true); setXlsError(''); setXlsResult(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file); fd.append('empresa_id', empresaId);
      fd.append('periodo', periodoSel); fd.append('tipo_operacion', tipoOp);
      fd.append('generar_asientos', generarAsientos ? '1' : '0');
      const r = await axios.post(`${API}/importar-sire`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setXlsResult(r.data); setXlsPreview(null); setFile(null);
    } catch (e) { setXlsError(e.response?.data?.error || 'Error al importar'); }
    setXlsLoading(false);
  }

  // ── API tab handlers ──
  const claveSolGuardada = !!empresaConfig?.sunat_clave_sol_configurada;

  async function handleApiPreview() {
    if (!empresaId || !periodoSel) { setApiError('Selecciona empresa y período'); return; }
    if (!claveSol.trim() && !claveSolGuardada) { setApiError('Ingresa tu Clave SOL'); return; }
    setApiLoading(true); setApiError(''); setApiPreview(null);
    try {
      const r = await axios.post(`${API}/importar-sire-api`, {
        empresa_id: empresaId, periodo: periodoSel, tipo_operacion: tipoOp,
        clave_sol: claveSol || undefined, solo_preview: true
      });
      setApiPreview(r.data);
    } catch (e) { setApiError(e.response?.data?.error || 'Error al consultar la API de SUNAT'); }
    setApiLoading(false);
  }

  async function handleDescargarExcelSire() {
    if (!empresaId || !periodoSel || (!claveSol.trim() && !claveSolGuardada)) { setApiError('Completa todos los campos'); return; }
    setApiDescargando(true); setApiError('');
    try {
      const r = await axios.get(`${API}/importar-sire-api/excel`, {
        params: { empresa_id: empresaId, periodo: periodoSel, tipo_operacion: tipoOp, clave_sol: claveSol || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `SIRE_${tipoOp}_${periodoSel}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // El error viene como blob — hay que leerlo como texto para mostrar el mensaje real
      let msg = 'Error al generar el Excel desde la API de SUNAT';
      try { msg = JSON.parse(await e.response.data.text())?.error || msg; } catch (_) {}
      setApiError(msg);
    }
    setApiDescargando(false);
  }

  const apiConfigurado = !!(empresaConfig?.sunat_client_id && empresaConfig?.sunat_client_secret && empresaConfig?.sunat_usuario_sol);

  const totalBase = (d) => d?.docs?.reduce((s, x) => s + (x.base_gravada || 0), 0) || 0;
  const totalIgv  = (d) => d?.docs?.reduce((s, x) => s + (x.igv || 0), 0) || 0;
  const totalMonto= (d) => d?.docs?.reduce((s, x) => s + (x.total || 0), 0) || 0;

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={1}>Importador SIRE / SUNAT</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Importa el Registro de Ventas o Compras desde SIRE. Puedes subir el archivo Excel o traerlo directamente desde la API de SUNAT.
      </Typography>

      {/* ── Filtros compartidos ── */}
      <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId}
              onChange={e => handleEmpresaChange(e.target.value)} sx={{ minWidth: '22em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Período" value={periodoSel}
              onChange={e => setPeriodoSel(e.target.value)} disabled={!empresaId} sx={{ minWidth: '14em' }}>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Tipo" value={tipoOp}
              onChange={e => { setTipoOp(e.target.value); setXlsPreview(null); setFile(null); setApiPreview(null); }} sx={{ minWidth: '16em' }}>
              <MenuItem value="VENTA">Registro de Ventas</MenuItem>
              <MenuItem value="COMPRA">Registro de Compras</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Tabs ── */}
      <Paper sx={{ borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<UploadFileIcon fontSize="small" />} iconPosition="start" label="Archivo Excel" />
          <Tab icon={<ApiIcon fontSize="small" />} iconPosition="start" label="Desde API SUNAT" />
        </Tabs>

        {/* ══ TAB 0: Excel ══ */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={12} md={8}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
                    {file ? file.name : 'Seleccionar archivo Excel (.xlsx)'}
                    <input type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
                  </Button>
                  <Button variant="outlined" startIcon={<VisibilityIcon />}
                    disabled={!file || !empresaId || !periodoSel || xlsLoading} onClick={handleXlsPreview}>
                    Vista previa
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={<Switch checked={generarAsientos} onChange={e => setGenerarAsientos(e.target.checked)} color="primary" />}
                label="Generar asientos contables automáticamente"
              />
            </Box>

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">Instrucciones SIRE:</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                1. Ingresa a <strong>sunat.gob.pe → SIRE</strong> → Registro de {tipoOp === 'VENTA' ? 'Ventas e Ingresos' : 'Compras'}<br />
                2. Selecciona el período → <strong>Exportar → Excel</strong><br />
                3. Sube ese archivo aquí. El sistema detecta automáticamente las columnas del formato SIRE estándar.
              </Typography>
            </Box>

            {xlsLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}
            {xlsError && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setXlsError('')}>{xlsError}</Alert>}

            {xlsResult && (
              <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                <Typography fontWeight="bold">Importación completada</Typography>
                <Typography variant="body2">{xlsResult.importados} documentos importados de {xlsResult.total} procesados.
                  {xlsResult.errores?.length > 0 && ` ${xlsResult.errores.length} advertencias.`}
                </Typography>
                {xlsResult.errores?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {xlsResult.errores.slice(0, 5).map((e, i) => <Typography key={i} variant="caption" display="block" color="text.secondary">{e}</Typography>)}
                    {xlsResult.errores.length > 5 && <Typography variant="caption">...y {xlsResult.errores.length - 5} más</Typography>}
                  </Box>
                )}
              </Alert>
            )}

            {xlsPreview && (
              <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Vista previa: {xlsPreview.total} documentos encontrados</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Base Gravada: S/ {fmt(totalBase(xlsPreview))} | IGV: S/ {fmt(totalIgv(xlsPreview))} | Total: S/ {fmt(totalMonto(xlsPreview))}
                    </Typography>
                    {xlsPreview.errores?.length > 0 && (
                      <Typography variant="caption" color="warning.main">{xlsPreview.errores.length} filas ignoradas por errores</Typography>
                    )}
                  </Box>
                  <Button variant="contained" color="success" startIcon={<CloudUploadIcon />} onClick={handleXlsImportar}>
                    Importar {xlsPreview.total} documentos
                  </Button>
                </Paper>
                <PreviewTable docs={xlsPreview.docs} tipoOp={tipoOp} />
                {xlsPreview.errores?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Filas con advertencias (serán ignoradas):</Typography>
                    {xlsPreview.errores.slice(0, 10).map((e, i) => <Typography key={i} variant="caption" display="block">{e}</Typography>)}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ══ TAB 1: API SUNAT ══ */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Estado de configuración API */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: apiConfigurado ? '#a5d6a7' : '#ffcc80', bgcolor: apiConfigurado ? '#f1f8e9' : '#fff8e1' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {apiConfigurado
                  ? <CheckCircleOutlineIcon color="success" />
                  : <ErrorOutlineIcon sx={{ color: 'warning.main' }} />
                }
                <Box sx={{ flex: 1 }}>
                  {apiConfigurado ? (
                    <>
                      <Typography variant="subtitle2" color="success.dark" fontWeight="bold">
                        API SUNAT configurada para esta empresa
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Client ID: {empresaConfig?.sunat_client_id?.substring(0, 8)}...  |  Usuario SOL: {empresaConfig?.sunat_usuario_sol}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ color: 'warning.dark' }} fontWeight="bold">
                        {!empresaId ? 'Selecciona una empresa para ver su configuración API' : 'API SUNAT no configurada para esta empresa'}
                      </Typography>
                      {empresaId && (
                        <Typography variant="caption" color="text.secondary">
                          Ve a <strong>Empresas Contables</strong> → edita la empresa → sección <strong>"Configuración API SUNAT"</strong> y completa Client ID, Client Secret y Usuario SOL.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
                {empresaId && !apiConfigurado && (
                  <Tooltip title="Ir a Empresas Contables">
                    <SettingsIcon sx={{ color: 'text.secondary' }} />
                  </Tooltip>
                )}
              </Box>
            </Paper>

            {/* Campo Clave SOL */}
            <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
              <Grid item xs={12} sm="auto">
                <TextField
                  size="small" label="Clave SOL" type={showClave ? 'text' : 'password'}
                  value={claveSol} onChange={e => setClaveSol(e.target.value)}
                  disabled={!apiConfigurado || !empresaId}
                  placeholder={claveSolGuardada ? 'Ya guardada — déjalo en blanco para usarla' : 'Tu clave SOL de SUNAT'}
                  sx={{ minWidth: '20em' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" color="action" /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowClave(!showClave)} edge="end">
                          {showClave ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  helperText={claveSolGuardada
                    ? 'Ya hay una clave guardada para esta empresa — escribe una nueva solo si quieres reemplazarla'
                    : 'La clave SOL se puede guardar en "Empresas Contables" para no pedirla cada vez'}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 2 }}>
              Primero una vista previa para confirmar que son los documentos correctos. Luego descarga un Excel
              ya prellenado con esos documentos — ahí eliges la cuenta contable de cada uno (o la dejas en blanco
              para usar la cuenta por defecto de Parámetros) — y lo subes con el botón <strong>"Importar Excel"</strong> de
              la otra pestaña para generar los asientos.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button variant="outlined" startIcon={apiLoading ? <CircularProgress size={16} /> : <VisibilityIcon />}
                disabled={!apiConfigurado || !empresaId || !periodoSel || (!claveSol.trim() && !claveSolGuardada) || apiLoading}
                onClick={handleApiPreview}>
                Vista previa (primeros 50)
              </Button>
              {apiPreview && (
                <Button variant="contained" color="success" startIcon={apiDescargando ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                  disabled={apiDescargando} onClick={handleDescargarExcelSire}>
                  Descargar Excel para asignar cuentas ({apiPreview.total} documentos)
                </Button>
              )}
            </Box>

            {apiError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>{apiError}</Alert>}

            {apiPreview && (
              <Box>
                <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Vista previa: {apiPreview.total} documentos en SUNAT — período {periodoSel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Base Gravada: S/ {fmt(totalBase(apiPreview))} | IGV: S/ {fmt(totalIgv(apiPreview))} | Total: S/ {fmt(totalMonto(apiPreview))}
                  </Typography>
                  {apiPreview.solo_preview && (
                    <Typography variant="caption" color="info.main">Mostrando primeros {apiPreview.docs?.length} documentos. Al importar se traerán todos.</Typography>
                  )}
                </Paper>
                <PreviewTable docs={apiPreview.docs || []} tipoOp={tipoOp} />
              </Box>
            )}

            {/* Info paso a paso */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #bbdefb' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                ¿Cómo configurar la API SUNAT para SIRE?
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <strong>1. Ingresa al Portal de Desarrolladores SUNAT</strong><br />
                &nbsp;&nbsp;&nbsp;→ <strong>sunat.gob.pe → Empresas → Solicitar Clave SOL</strong> (o usa tu clave SOL existente)<br /><br />
                <strong>2. Registra tu aplicación en SUNAT APIs</strong><br />
                &nbsp;&nbsp;&nbsp;→ Ingresa a <strong>api.sunat.gob.pe</strong> con tu Clave SOL<br />
                &nbsp;&nbsp;&nbsp;→ Menú: <strong>Aplicaciones → Nueva Aplicación</strong><br />
                &nbsp;&nbsp;&nbsp;→ Nombre: InsightSoft (o cualquier nombre)<br />
                &nbsp;&nbsp;&nbsp;→ Marca los scopes: <strong>SIRE Comprobantes</strong> y/o <strong>SIRE Compras</strong><br />
                &nbsp;&nbsp;&nbsp;→ También marca <strong>GRE (Guía de Remisión Electrónica)</strong> si usas ese módulo<br /><br />
                <strong>3. Copia el Client ID y Client Secret</strong><br />
                &nbsp;&nbsp;&nbsp;→ Guárdalos en InsightSoft → <strong>Empresas Contables → editar empresa</strong><br />
                &nbsp;&nbsp;&nbsp;→ El <strong>Usuario SOL</strong> es tu usuario SUNAT (sin RUC, ej: USERSOL01)<br /><br />
                <strong>4. En cada importación</strong> ingresa tu <strong>Clave SOL</strong> aquí.<br />
                &nbsp;&nbsp;&nbsp;La clave nunca se guarda en el sistema — se usa solo para autenticarte con SUNAT.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
